import React, { useRef } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import PanelEmptyState from "./PanelEmptyState";
import "./PanelBase.css";
import "./OrdersPanel.css";
import { usePositionsData } from "./hooks/usePositionsData.js";
import { useRouterStatus } from "../../hooks/useRouterStatus.js";
import { useOrderStore } from "../../store/orderStore.jsx";
import { useThemeStore } from "../../store/themeStore.jsx";
import { isDarkTheme } from "../../config/themes";
import {
  cancelAllLocalOrders,
  cancelLocalOrdersForSymbol,
  closeAllPositions,
  closePositionsForSymbol,
} from "../../utils/orderCancellation.js";
import {
  confirmCloseAllPositions,
  confirmClosePosition,
} from "../../utils/confirmationUtils.js";
import { formatPrice } from "../../utils/priceUtils.js";
import { formatNumber } from "../../utils/numberFormatting.js";
import { getNumericFontFamily } from "../../utils/fontUtils.js";
import { PanelToolbar, PanelToolbarHost } from "./PanelToolbar";
import useHostDocument from "~/hooks/useHostDocument";

// Displays positions list with release button (data via usePositionsData hook)
const PositionsPanel = ({
  initialToolbarPinned = true,
  onToolbarPinnedChange,
  claimFocus,
}) => {
  const { positions } = usePositionsData();
  const theme = useThemeStore((state) => state.theme);
  const { isConnected: isRouterConnected } = useRouterStatus();

  // PoC #popout: document the panel currently lives in (menu portal target)
  const [hostDoc, hostProbe] = useHostDocument();
  // Menu state for actions dropdown (same pattern as WorkspaceMenus)
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const [toolbarPinned, setToolbarPinned] =
    React.useState(initialToolbarPinned);
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [menuPositionData, setMenuPositionData] = React.useState(null);
  const [menuCoords, setMenuCoords] = React.useState({ top: 0, left: 0 });

  // Handle clicks outside menu to close it (always active)
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuVisible(false);
        setMenuPositionData(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get order store for canceling local orders - use selectors to prevent re-renders
  const ordersBySymbol = useOrderStore((state) => state.ordersBySymbol);
  const removeOrder = useOrderStore((state) => state.removeOrder);

  const handleReleasePositions = async () => {
    confirmCloseAllPositions(async () => {
      try {
        console.log(
          "🚀 Fechando todas as posições e cancelando ordens locais...",
        );

        // Cancel all local orders using centralized utility
        cancelAllLocalOrders(ordersBySymbol, removeOrder);

        // Close all positions using centralized utility
        await closeAllPositions();

        console.log("🏁 Processo de fechamento global completado com sucesso");
      } catch (err) {
        console.error("❌ Erro ao zerar/cancelar posições:", err);
        alert(`Erro ao zerar/cancelar posições: ${err.message}`);
      }
    });
  };

  // Handle individual position closing with local order cancellation
  const handleClosePosition = async (position) => {
    confirmClosePosition(position.symbol, async () => {
      try {
        console.log(
          `🚀 Zerando/cancelando posição e cancelando ordens locais do símbolo: ${position.symbol}`,
        );

        // Cancel local orders for this symbol using centralized utility
        cancelLocalOrdersForSymbol(
          position.symbol,
          ordersBySymbol,
          removeOrder,
        );

        // Close positions for this symbol using centralized utility
        await closePositionsForSymbol(position.symbol);

        console.log(
          `✅ Posição e ordens locais do ${position.symbol} processadas com sucesso`,
        );
      } catch (err) {
        console.error("❌ Erro ao zerar/cancelar posição:", err);
        alert(`Erro ao zerar/cancelar posição: ${err.message}`);
      }
    });
  };

  const isDarkMode = isDarkTheme(theme);
  const handleToolbarPinnedChange = React.useCallback(
    (pinned) => {
      setToolbarPinned(pinned);
      onToolbarPinnedChange?.(pinned);
    },
    [onToolbarPinnedChange],
  );

  return (
    <PanelToolbarHost
      pinned={toolbarPinned}
      onMouseDown={claimFocus}
      className="vertical-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        // Token-driven: navy on Extra Dark, slate on lara-dark, none on light.
        backgroundColor: isDarkMode
          ? "var(--tz-surface-deep, #1f2937)"
          : undefined,
      }}
      contentStyle={{
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        overflow: "hidden",
      }}
      toolbar={
        <PanelToolbar
          pinned={toolbarPinned}
          onPinnedChange={handleToolbarPinnedChange}
          pinDataEvoId="positionspanel-btn-toolbar-pin"
          right={
            <Button
              label="Zerar/Cancelar"
              className="btn-vertical-panel"
              onClick={handleReleasePositions}
              data-evo-id="positionspanel-btn-release-cancel"
            />
          }
        >
          <h5>Posições</h5>
        </PanelToolbar>
      }
    >
      {positions.length === 0 ? (
        <PanelEmptyState
          message="Nenhuma posição"
          isRouterConnected={isRouterConnected}
          dataEvoId="positionspanel-link-router-login"
        />
      ) : (
        <DataTable
          value={positions}
          className="posicoes-datatable log-datatable"
          scrollable
          scrollHeight="flex"
          scrollDirection="vertical"
          size="small"
          stripedRows
        >
          <Column
            field="symbol"
            header="Símbolo"
            style={{ width: "20%" }}
            body={(rowData) => (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "var(--text-color)",
                }}
              >
                {rowData.symbol}
              </span>
            )}
          />
          <Column
            field="qtd"
            header="Qtd"
            style={{ width: "12%", textAlign: "right" }}
            headerClassName="header-right"
            body={(rowData) => (
              <span
                style={{
                  color: rowData.qtd < 0 ? "#dc2626" : "#16a34a",
                  fontSize: "11px",
                  fontWeight: "600",
                  fontFamily: getNumericFontFamily(),
                }}
              >
                {formatNumber(rowData.qtd, 0)}
              </span>
            )}
          />
          <Column
            field="price"
            header="Preço"
            style={{ width: "30%", textAlign: "right" }}
            headerClassName="header-right"
            body={(rowData) => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "1px",
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    color: "var(--text-color)",
                    fontFamily: getNumericFontFamily(),
                  }}
                >
                  {formatPrice(rowData.symbol, rowData.price)}
                </span>
                <span
                  style={{
                    fontSize: "9px",
                    color: "#9ca3af",
                    fontFamily: getNumericFontFamily(),
                  }}
                >
                  {rowData.currentPrice !== undefined
                    ? formatPrice(rowData.symbol, rowData.currentPrice)
                    : "-"}
                </span>
              </div>
            )}
          />
          <Column
            field="saldo"
            header="Saldo"
            style={{ width: "22%", textAlign: "right" }}
            headerClassName="header-right"
            body={(rowData) =>
              rowData.saldo === null ? (
                // FOQ do servidor indisponível → sem R$ chutado
                <span
                  style={{
                    color: "#6b7280",
                    fontSize: "11px",
                    fontFamily: getNumericFontFamily(),
                  }}
                >
                  —
                </span>
              ) : (
                <span
                  style={{
                    color: rowData.saldo >= 0 ? "#16a34a" : "#dc2626",
                    fontSize: "11px",
                    fontWeight: "600",
                    fontFamily: getNumericFontFamily(),
                  }}
                >
                  {rowData.saldo >= 0 ? "+" : ""}
                  {formatNumber(rowData.saldo, 2)}
                </span>
              )
            }
          />
          <Column
            header=""
            style={{ width: "8%", textAlign: "center" }}
            headerClassName="header-center"
            body={(rowData) => (
              <Button
                icon="pi pi-cog"
                size="small"
                text
                rounded
                className="gear-btn"
                style={{
                  width: "22px",
                  height: "22px",
                  fontSize: "12px",
                  padding: "0",
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  menuButtonRef.current = e.currentTarget;
                  setMenuPositionData(rowData);
                  setMenuCoords({ top: rect.bottom + 2, left: rect.right });
                  setMenuVisible(true);
                }}
                aria-haspopup
                data-evo-id="positionspanel-btn-actions"
              />
            )}
          />
        </DataTable>
      )}

      {/* Custom Menu - same pattern as WorkspaceMenus */}
      {hostProbe}
      {menuVisible &&
        menuPositionData &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="p-menu p-component p-menu-overlay"
            style={{
              position: "fixed",
              top: menuCoords.top,
              left: menuCoords.left,
              transform: "translateX(-100%)",
              minWidth: 140,
              zIndex: 10000,
              backgroundColor: "var(--surface-overlay)",
              borderRadius: "4px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
              border: "1px solid var(--surface-border)",
            }}
            onMouseLeave={() => {
              setMenuVisible(false);
              setMenuPositionData(null);
            }}
          >
            <ul
              className="p-menu-list"
              style={{ margin: 0, padding: "0.25rem 0", listStyle: "none" }}
            >
              <li className="p-menuitem">
                <a
                  className="p-menuitem-link"
                  onClick={(e) => {
                    e.preventDefault();
                    const position = menuPositionData;
                    setMenuVisible(false);
                    setMenuPositionData(null);

                    // Validate position still exists
                    if (!position || !position.symbol) {
                      console.warn(
                        "Position no longer exists, skipping action",
                      );
                      return;
                    }
                    const stillExists = positions.some(
                      (p) => p.symbol === position.symbol,
                    );
                    if (!stillExists) {
                      console.warn(
                        "Position was removed, skipping action:",
                        position.symbol,
                      );
                      return;
                    }
                    handleClosePosition(position);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 1rem",
                    cursor: "pointer",
                  }}
                  data-evo-id="positionspanel-link-context-menu-item"
                >
                  <span className="p-menuitem-icon pi pi-times" />
                  <span className="p-menuitem-text">Zerar Posição</span>
                </a>
              </li>
            </ul>
          </div>,
          (hostDoc ?? document).body,
        )}
    </PanelToolbarHost>
  );
};

PositionsPanel.propTypes = {
  initialToolbarPinned: PropTypes.bool,
  onToolbarPinnedChange: PropTypes.func,
  claimFocus: PropTypes.func,
};

export default React.memo(PositionsPanel);

// (No external props currently)
